import React, { useEffect, useState } from "react";
import { Application, Program } from "../../../core/types/types";
import { useSelector } from "react-redux";
import apiInstance from "../../../utility/apiInstance";
import { getCurrencySymbol } from "../../../utility/currencyUtils";

interface PopupProps {
    show: boolean;
    onClose: () => void;
    application: Application | null;
    updateApplicationsHandler: any;
}

const ApplicationDetailsPopup: React.FC<PopupProps> = ({ show, onClose, application, updateApplicationsHandler }) => {

    const dateOptions: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric",
    };

    const user = useSelector((state: any) => state.auth.user);

    const [tasks, setTasks] = useState<any[]>([]);
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [newTaskTitle, setNewTaskTitle] = useState("");
    const [newTaskDescription, setNewTaskDescription] = useState("");
    const [isCreatingTask, setIsCreatingTask] = useState(false);

    const fetchTasks = async () => {
        if (application) {
            try {
                const response = await apiInstance.get("/api/tasks", {
                    params: {
                        "where[application][equals]": application.id,
                    },
                });
                setTasks(response.data.docs || []);
            } catch (error) {
                console.error("Error fetching application tasks:", error);
            }
        }
    };

    useEffect(() => {
        if (show) {
            fetchTasks();
            setShowTaskForm(false);
            setNewTaskTitle("");
            setNewTaskDescription("");
        } else {
            setTasks([]);
        }
    }, [show, application]);

    const handleCreateTask = async () => {
        if (!newTaskTitle.trim()) return;
        setIsCreatingTask(true);
        try {
            await apiInstance.post("/api/tasks", {
                title: newTaskTitle,
                description: newTaskDescription,
                status: 'pending',
                application: application?.id,
                owner: user.id
            });
            await fetchTasks();
            setShowTaskForm(false);
            setNewTaskTitle("");
            setNewTaskDescription("");
        } catch (error) {
            console.error("Error creating task:", error);
        } finally {
            setIsCreatingTask(false);
        }
    };

    const updateTaskStatus = async (taskId: string, status: string) => {
        try {
            await apiInstance.patch(`/api/tasks/${taskId}`, { status });
            setTasks((prevTasks) => prevTasks.map(t => t.id === taskId ? { ...t, status } : t));
        } catch (error) {
            console.error("Error updating task status:", error);
        }
    };

    const [msg, setMsg] = useState({
        error: false,
        loading: false,
        text: "",
    });

    const updateApplicationStatus = async (status: string) => {
        setMsg({
            error: false,
            loading: true,
            text: "",
        });

        try {

            if (application) {

                const res = await apiInstance.patch(`/api/applications/${application.id}`, {
                    ...application,
                    status: status
                });
                updateApplicationsHandler(res.data.doc)

                setMsg({
                    error: false,
                    loading: false,
                    text: `Application ${status} successfully!`,
                });

            }

            if (status === "approved" || status === "declined") {
                setTimeout(onClose, 3000);
            }

        } catch (err: any) {
            setMsg({
                error: true,
                loading: false,
                text: err.response?.data?.errors?.[0]?.message || err.message,
            });
        }
    }

    const updatePaymentStatus = async (isPaid: boolean) => {
        setMsg({ error: false, loading: true, text: "" });
        try {
            if (application) {
                const res = await apiInstance.patch(`/api/applications/${application.id}`, {
                    isApplicationFeePaid: isPaid
                });
                updateApplicationsHandler(res.data.doc);
                setMsg({ error: false, loading: false, text: "Payment status updated!" });
            }
        } catch (err: any) {
            setMsg({ error: true, loading: false, text: err.message });
        }
    };

    const updateCommissionStatus = async (status: string) => {
        setMsg({ error: false, loading: true, text: "" });
        try {
            if (application) {
                const res = await apiInstance.patch(`/api/applications/${application.id}`, {
                    commissionStatus: status
                });
                updateApplicationsHandler(res.data.doc);
                setMsg({ error: false, loading: false, text: "Commission status updated!" });
            }
        } catch (err: any) {
            setMsg({ error: true, loading: false, text: err.message });
        }
    };

    return (
        <div
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex={-1}
            role="dialog"
            style={{ background: show ? "rgba(0,0,0,0.5)" : "transparent" }}
        >
            <div className="modal-dialog-centered w-50 m-auto" role="document">
                <div className="modal-content">
                    {/* Header */}
                    <div className="modal-header">
                        <h5 className="modal-title text-secondary">Program Application</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                        ></button>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        <h5 className="fw-bold mb-5 text-center">{application?.program?.programName || ''}</h5>

                        <div className="course-page-content pt-0">
                            <div className="card mb-4 shadow-sm border-0 rounded-3">
                                <div className="card-body">

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Status:</div>
                                        <div className="col-sm-8">
                                            <div className={
                                                application?.status === "pending"
                                                    ? "text-warning fw-bold"
                                                    : application?.status === "in_review"
                                                        ? "text-info fw-bold"
                                                        : application?.status === "approved"
                                                            ? "text-success fw-bold"
                                                            : application?.status === "declined"
                                                                ? "text-danger fw-bold"
                                                                : ""
                                            } >
                                                {application?.status || 'pending'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Applicant:</div>
                                        <div className="col-sm-8">
                                            {application?.student?.firstName || ''} {application?.student?.lastName || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Program:</div>
                                        <div className="col-sm-8">
                                            {application?.program?.programName || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Institution:</div>
                                        <div className="col-sm-8">
                                            {application?.institution?.schoolName || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Application Date:</div>
                                        <div className="col-sm-8">
                                            {application?.createdAt
                                                ? new Date(application?.createdAt).toLocaleDateString(
                                                    "en-IN",
                                                    dateOptions
                                                )
                                                : "-"}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Program Start Date:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.program?.start
                                                ? String(application.program.start)
                                                : "-"}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Program End Date:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.program?.end
                                                ? String(application.program.end)
                                                : "-"}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Application Fee:</div>
                                        <div className="col-sm-8 text-primary fw-bold">
                                            {application?.applicationFee ? `${getCurrencySymbol(application?.institution?.country)}${application.applicationFee}` : "N/A"}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Fee Status:</div>
                                        <div className="col-sm-8">
                                            {application?.isApplicationFeePaid ? (
                                                <span className="text-success fw-bold">Paid</span>
                                            ) : (
                                                <span className="text-danger fw-bold">Unpaid</span>
                                            )}
                                        </div>
                                    </div>

                                    {application?.commissionAmount && (
                                        <div className="row mb-2">
                                            <div className="col-sm-4 fw-semibold text-muted">Commission:</div>
                                            <div className="col-sm-8">
                                                <span className="fw-bold">${application.commissionAmount}</span>
                                                <span className={`ms-2 badge ${application.commissionStatus === 'paid' ? 'bg-success' : application.commissionStatus === 'approved' ? 'bg-info' : 'bg-warning text-dark'}`}>
                                                    {application.commissionStatus?.toUpperCase() || 'PENDING'}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>

                        <div className="course-page-content pt-0">
                            <div className="card mb-4 shadow-sm border-0 rounded-3">
                                <div className="card-body">
                                    <h5 className="mb-3 text-primary">Student Details</h5>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Name:</div>
                                        <div className="col-sm-8">
                                            {application?.student?.firstName || ''} {application?.student?.lastName || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Gender:</div>
                                        <div className="col-sm-8">
                                            {application?.student?.gender || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">First Language:</div>
                                        <div className="col-sm-8">
                                            {application?.student?.firstLanguage || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Country of Citizenship:</div>
                                        <div className="col-sm-8">
                                            {application?.student?.countryOfCitizenship || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Marital Status:</div>
                                        <div className="col-sm-8">
                                            {application?.student?.maritalStatus || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Date of Birth:</div>
                                        <div className="col-sm-8">
                                            {application?.student?.dob
                                                ? new Date(application?.student?.dob).toLocaleDateString(
                                                    "en-IN",
                                                    dateOptions
                                                )
                                                : "-"}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Address:</div>
                                        <div className="col-sm-8">
                                            {[
                                                application?.student?.streetAddress,
                                                application?.student?.city,
                                                application?.student?.state,
                                                application?.student?.country,
                                                application?.student?.zipCode,
                                            ]
                                                .filter(Boolean) // remove empty/undefined values
                                                .join(", ")}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Contact:</div>
                                        <div className="col-sm-8">
                                            {application?.student?.phone || ''}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        <div className="course-page-content pt-0">
                            <div className="card mb-4">
                                <div className="card-body">
                                    <h5 className="mb-3 text-primary">Program Details</h5>
                                    {application?.program?.about || ""}
                                </div>
                            </div>
                        </div>
                        {application?.program?.requirement && (
                            <div className="course-page-content pt-0">
                                <div className="card mb-4 border-0 rounded-3 shadow-sm">
                                    <div className="card-body">
                                        <h5 className="mb-3 text-primary">Program Requirements</h5>
                                        {application?.program?.requirement || ""}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="course-page-content pt-0">
                            <div className="card mb-4 shadow-sm border-0 rounded-3">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0 text-primary">Application Tasks</h5>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => setShowTaskForm(!showTaskForm)}
                                        >
                                            {showTaskForm ? "Cancel" : "+ Add Task"}
                                        </button>
                                    </div>

                                    {showTaskForm && (
                                        <div className="mb-4 p-3 bg-light rounded border">
                                            <div className="mb-2">
                                                <label className="form-label small fw-semibold">Task Title <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="e.g. Needs translated transcripts"
                                                    value={newTaskTitle}
                                                    onChange={e => setNewTaskTitle(e.target.value)}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label small fw-semibold">Description</label>
                                                <textarea
                                                    className="form-control form-control-sm"
                                                    rows={2}
                                                    placeholder="Optional details..."
                                                    value={newTaskDescription}
                                                    onChange={e => setNewTaskDescription(e.target.value)}
                                                />
                                            </div>
                                            <div className="text-end">
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={handleCreateTask}
                                                    disabled={isCreatingTask || !newTaskTitle.trim()}
                                                >
                                                    {isCreatingTask ? "Creating..." : "Save Task"}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {tasks && tasks.length > 0 ? (
                                        <ul className="list-group list-group-flush">
                                            {tasks.map((task: any) => (
                                                <li key={task.id} className="list-group-item px-0 border-bottom-0 mb-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <strong className="text-dark">{task.title}</strong>
                                                        <div className="d-flex align-items-center">
                                                            <span className={`badge rounded-pill ${task.status === 'completed' ? 'bg-success' : (task.status === 'in_progress' || task.status === 'in_review') ? 'bg-info' : 'bg-warning'}`}>
                                                                {task.status ? task.status.replace('_', ' ').toUpperCase() : 'PENDING'}
                                                            </span>
                                                            {task.status !== 'completed' && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-success ms-2 py-0 px-2"
                                                                    style={{ fontSize: '0.75rem' }}
                                                                    onClick={() => updateTaskStatus(task.id, 'completed')}
                                                                >
                                                                    Mark Completed
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {task.description && <p className="mb-0 text-muted small">{task.description}</p>}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        !showTaskForm && <p className="text-muted small mb-0">No tasks created yet.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer d-flex gap-3">
                        {msg.loading && (
                            <span className="mt-3 fs-14 fw-normal d-flex align-items-center justify-content-center">
                                Upating application status...
                            </span>
                        )}
                        {!msg.loading && (
                            <span
                                className={`mt-3 ${msg.error ? "text-red" : ""} fs-14 fw-normal d-flex align-items-center justify-content-center`}
                            >
                                {msg.text}
                            </span>
                        )}
                        {user?.role === 'inst_admin' && (
                            <div className="d-flex gap-2 me-auto">
                                {!application?.isApplicationFeePaid && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-success btn-sm"
                                        onClick={() => updatePaymentStatus(true)}
                                    >
                                        Mark Fee Paid
                                    </button>
                                )}
                                {application?.isApplicationFeePaid && application?.commissionStatus === 'pending' && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-info btn-sm"
                                        onClick={() => updateCommissionStatus('approved')}
                                    >
                                        Approve Commission
                                    </button>
                                )}
                            </div>
                        )}
                        {(application?.status === 'pending' || application?.status === 'in_review') && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => updateApplicationStatus("approved")}
                                >
                                    Approve
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => updateApplicationStatus("declined")}
                                >
                                    Decline
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationDetailsPopup;
